import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Settings, Save, Loader2, ShoppingCart, ClipboardList, Calendar, MapPin, MapPinOff, Repeat, PauseCircle, Mail, Mic, Inbox, Hash, Send, MessageSquare, RotateCcw, FileText, FileCheck, Smartphone, AlertTriangle } from "lucide-react";

const WEEKDAYS = [
  { code: "mon", label: "Mon" },
  { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" },
  { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" },
  { code: "sat", label: "Sat" },
  { code: "sun", label: "Sun" },
];
const TZ_LABELS = {
  "America/New_York": "Eastern (ET)",
  "America/Chicago": "Central (CT)",
  "America/Denver": "Mountain (MT)",
  "America/Phoenix": "Arizona (MST)",
  "America/Los_Angeles": "Pacific (PT)",
  "America/Anchorage": "Alaska (AKT)",
  "Pacific/Honolulu": "Hawaii (HST)",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailConfigRow = ({ label, description, icon: Icon, value, onChange, onSave, saving, originalValue }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4 border-b border-[#1a3a6a] last:border-b-0">
    <div className="flex items-center gap-3 sm:w-64 flex-shrink-0">
      <div className="w-9 h-9 rounded-lg bg-[#f4cc1e]/10 flex items-center justify-center flex-shrink-0">
        <Icon size={16} className="text-[#f4cc1e]" />
      </div>
      <div>
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-white/50">{description}</p>
      </div>
    </div>
    <div className="flex flex-1 items-center gap-2">
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter email(s), comma-separated..."
        className="flex-1 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
      />
      <Button
        size="sm"
        onClick={onSave}
        disabled={saving || value === originalValue}
        className="h-9 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
      >
        {saving ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Save size={14} />
        )}
      </Button>
    </div>
  </div>
);

const CHANGE_REQUEST_TYPES = [
  { key: "criteria", label: "Criteria Changes", description: "Criteria change requests from customers", icon: ClipboardList },
  { key: "schedule", label: "Schedule Changes", description: "Schedule change requests from customers", icon: Calendar },
  { key: "add_zips", label: "Add Zip Codes", description: "Zip code addition requests from customers", icon: MapPin },
  { key: "remove_zips", label: "Remove Zip Codes", description: "Zip code removal requests from customers", icon: MapPinOff },
  { key: "replace_zips", label: "Replace Zip Codes", description: "Zip code replacement requests from customers", icon: Repeat },
  { key: "pause_resume", label: "Pause / Resume Campaign", description: "Pause/resume campaign requests from customers", icon: PauseCircle },
  { key: "update_recipients", label: "Update Lead Recipients", description: "Lead recipient email change requests from customers", icon: Mail },
  { key: "recordings", label: "Recordings", description: "Recordings on/off requests from customers", icon: Mic },
];

const AdminSettings = () => {
  // Order More Notifications
  const [orderMoreEmail, setOrderMoreEmail] = useState("");
  const [orderMoreEmailInput, setOrderMoreEmailInput] = useState("");
  const [savingOrderMore, setSavingOrderMore] = useState(false);

  // Approvals notification email
  const [approvalsEmail, setApprovalsEmail] = useState("");
  const [approvalsEmailInput, setApprovalsEmailInput] = useState("");
  const [savingApprovals, setSavingApprovals] = useState(false);

  // Reactivation requests email
  const [reactivationEmail, setReactivationEmail] = useState("");
  const [reactivationEmailInput, setReactivationEmailInput] = useState("");
  const [savingReactivation, setSavingReactivation] = useState(false);

  // Terms accepted notification email
  const [termsEmail, setTermsEmail] = useState("");
  const [termsEmailInput, setTermsEmailInput] = useState("");
  const [savingTermsEmail, setSavingTermsEmail] = useState(false);

  // No-lead (72h) alert recipient
  const [noLeadsEmail, setNoLeadsEmail] = useState("");
  const [noLeadsEmailInput, setNoLeadsEmailInput] = useState("");
  const [savingNoLeads, setSavingNoLeads] = useState(false);

  // How it Works sections
  const [sections, setSections] = useState([]);
  const [sectionsSaved, setSectionsSaved] = useState("[]");
  const [savingTerms, setSavingTerms] = useState(false);

  // Change Request Routing Emails
  const [crEmails, setCrEmails] = useState({ criteria: "", schedule: "", add_zips: "", remove_zips: "" });
  const [crEmailInputs, setCrEmailInputs] = useState({ criteria: "", schedule: "", add_zips: "", remove_zips: "" });
  const [crSaving, setCrSaving] = useState({});

  // Slack chat webhook
  const [slackWebhook, setSlackWebhook] = useState("");
  const [slackWebhookInput, setSlackWebhookInput] = useState("");
  const [slackMention, setSlackMention] = useState("");
  const [slackMentionInput, setSlackMentionInput] = useState("");
  const [savingSlack, setSavingSlack] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);

  // Twilio SMS
  const [twilio, setTwilio] = useState({ from_number: "", enabled: false, credentials_configured: false });
  const [twilioFromInput, setTwilioFromInput] = useState("");
  const [savingTwilio, setSavingTwilio] = useState(false);
  const [twilioTestNumber, setTwilioTestNumber] = useState("");
  const [testingTwilio, setTestingTwilio] = useState(false);

  // SMS Slack notifications (dedicated channel)
  const [smsSlackWebhook, setSmsSlackWebhook] = useState("");
  const [smsSlackWebhookInput, setSmsSlackWebhookInput] = useState("");
  const [smsSlackMention, setSmsSlackMention] = useState("");
  const [smsSlackMentionInput, setSmsSlackMentionInput] = useState("");
  const [savingSmsSlack, setSavingSmsSlack] = useState(false);
  const [testingSmsSlack, setTestingSmsSlack] = useState(false);

  // Chat auto-reply (business hours vs after hours)
  const [autoReply, setAutoReply] = useState({
    hours_message: "",
    afterhours_message: "",
    business_days: [],
    business_open: "10:00",
    business_close: "18:00",
    business_timezone: "America/New_York",
  });
  const [allowedTzs, setAllowedTzs] = useState(["America/New_York"]);
  const [savingAutoReply, setSavingAutoReply] = useState(false);

  // QuickBooks Online connection
  const [qbo, setQbo] = useState({ status: "loading" });
  const loadQbo = async () => {
    try {
      const { data } = await axios.get(`${API}/quickbooks/status`);
      setQbo(data);
    } catch { setQbo({ status: "disconnected" }); }
  };
  useEffect(() => { loadQbo(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("qbo");
    if (p === "connected") toast.success("QuickBooks connected");
    if (p === "error") toast.error("QuickBooks connection failed");
  }, []);
  const connectQbo = async () => {
    try {
      const { data } = await axios.get(`${API}/quickbooks/connect`);
      window.location.href = data.authorize_url;
    } catch { toast.error("Could not start QuickBooks connection"); }
  };
  const disconnectQbo = async () => {
    try {
      await axios.post(`${API}/quickbooks/disconnect`);
      toast.success("QuickBooks disconnected");
      loadQbo();
    } catch { toast.error("Could not disconnect"); }
  };
  const [inv, setInv] = useState({ company_name: "", description: "Lead generation", quantity: 1, unit_price: 0, item_id: "" });
  const [invSubmitting, setInvSubmitting] = useState(false);
  const createInvoice = async () => {
    setInvSubmitting(true);
    try {
      await axios.post(`${API}/quickbooks/invoices`, {
        company_name: inv.company_name,
        description: inv.description,
        quantity: Number(inv.quantity),
        unit_price: Number(inv.unit_price),
        item_id: inv.item_id || undefined,
      });
      toast.success("Invoice created in QuickBooks");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create invoice");
    } finally { setInvSubmitting(false); }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [orderRes, crRes, apprRes, slackRes, autoRes, reactRes, termsEmailRes, termsRes, twilioRes, smsRes, noLeadsRes] = await Promise.all([
          axios.get(`${API}/admin/app-settings/order-more-email`),
          axios.get(`${API}/admin/app-settings/change-request-emails`),
          axios.get(`${API}/admin/app-settings/approvals-email`),
          axios.get(`${API}/admin/app-settings/slack-chat-webhook`),
          axios.get(`${API}/admin/app-settings/chat-auto-reply`),
          axios.get(`${API}/admin/app-settings/reactivation-email`),
          axios.get(`${API}/admin/app-settings/terms-accepted-email`),
          axios.get(`${API}/admin/app-settings/terms`),
          axios.get(`${API}/admin/app-settings/twilio`),
          axios.get(`${API}/admin/app-settings/sms`),
          axios.get(`${API}/admin/app-settings/no-leads-alert-email`)
        ]);
        const orderEmail = orderRes.data.order_more_email || "";
        setOrderMoreEmail(orderEmail);
        setOrderMoreEmailInput(orderEmail);

        const emails = crRes.data || {};
        setCrEmails(emails);
        setCrEmailInputs({ ...emails });

        const apprEmail = apprRes.data?.approvals_notification_email || "";
        setApprovalsEmail(apprEmail);
        setApprovalsEmailInput(apprEmail);

        const reactEmail = reactRes.data?.reactivation_email || "";
        setReactivationEmail(reactEmail);
        setReactivationEmailInput(reactEmail);

        const tEmail = termsEmailRes.data?.terms_accepted_email || "";
        setTermsEmail(tEmail);
        setTermsEmailInput(tEmail);

        const nl = noLeadsRes.data?.no_leads_alert_email || "";
        setNoLeadsEmail(nl);
        setNoLeadsEmailInput(nl);

        const secs = termsRes.data?.sections || [];
        setSections(secs);
        setSectionsSaved(JSON.stringify(secs));

        if (twilioRes.data) {
          setTwilio(twilioRes.data);
          setTwilioFromInput(twilioRes.data.from_number || "");
        }

        if (smsRes.data) {
          setSmsSlackWebhook(smsRes.data.slack_webhook_url || "");
          setSmsSlackWebhookInput(smsRes.data.slack_webhook_url || "");
          setSmsSlackMention(smsRes.data.slack_mention || "");
          setSmsSlackMentionInput(smsRes.data.slack_mention || "");
        }

        const slackUrl = slackRes.data?.slack_chat_webhook_url || "";
        setSlackWebhook(slackUrl);
        setSlackWebhookInput(slackUrl);
        const slackMen = slackRes.data?.slack_chat_mention || "";
        setSlackMention(slackMen);
        setSlackMentionInput(slackMen);

        if (autoRes.data) {
          setAutoReply({
            hours_message: autoRes.data.hours_message || "",
            afterhours_message: autoRes.data.afterhours_message || "",
            business_days: autoRes.data.business_days || [],
            business_open: autoRes.data.business_open || "10:00",
            business_close: autoRes.data.business_close || "18:00",
            business_timezone: autoRes.data.business_timezone || "America/New_York",
          });
          setAllowedTzs(autoRes.data.allowed_timezones || ["America/New_York"]);
        }
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        toast.error("Failed to load settings.");
      }
    };
    fetchSettings();
  }, []);

  const toggleDay = (code) => {
    setAutoReply((prev) => ({
      ...prev,
      business_days: prev.business_days.includes(code)
        ? prev.business_days.filter((d) => d !== code)
        : [...prev.business_days, code],
    }));
  };

  const handleSaveAutoReply = async () => {
    if (!autoReply.hours_message.trim() || !autoReply.afterhours_message.trim()) {
      toast.error("Both auto-reply messages are required.");
      return;
    }
    setSavingAutoReply(true);
    try {
      await axios.put(`${API}/admin/app-settings/chat-auto-reply`, {
        hours_message: autoReply.hours_message,
        afterhours_message: autoReply.afterhours_message,
        business_days: autoReply.business_days,
        business_open: autoReply.business_open,
        business_close: autoReply.business_close,
        business_timezone: autoReply.business_timezone,
      });
      toast.success("Auto-reply settings saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save auto-reply settings.");
    } finally {
      setSavingAutoReply(false);
    }
  };

  const handleSaveSlack = async () => {
    setSavingSlack(true);
    try {
      await axios.put(`${API}/admin/app-settings/slack-chat-webhook`, {
        webhook_url: slackWebhookInput.trim(),
        mention: slackMentionInput.trim()
      });
      setSlackWebhook(slackWebhookInput.trim());
      setSlackMention(slackMentionInput.trim());
      toast.success(slackWebhookInput.trim() ? "Slack settings saved!" : "Slack notifications disabled.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Slack webhook.");
    } finally {
      setSavingSlack(false);
    }
  };

  const handleTestSlack = async () => {
    setTestingSlack(true);
    try {
      await axios.post(`${API}/admin/app-settings/slack-chat-webhook/test`);
      toast.success("Test message sent to Slack!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test message.");
    } finally {
      setTestingSlack(false);
    }
  };

  const handleSaveSmsSlack = async () => {
    setSavingSmsSlack(true);
    try {
      await axios.put(`${API}/admin/app-settings/sms`, {
        slack_webhook_url: smsSlackWebhookInput.trim(),
        slack_mention: smsSlackMentionInput.trim(),
      });
      setSmsSlackWebhook(smsSlackWebhookInput.trim());
      setSmsSlackMention(smsSlackMentionInput.trim());
      toast.success(smsSlackWebhookInput.trim() ? "SMS Slack settings saved!" : "SMS Slack notifications will use the chat channel.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save SMS Slack settings.");
    } finally {
      setSavingSmsSlack(false);
    }
  };

  const handleTestSmsSlack = async () => {
    setTestingSmsSlack(true);
    try {
      await axios.post(`${API}/admin/app-settings/sms/slack-test`);
      toast.success("Test message sent to Slack!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test message.");
    } finally {
      setTestingSmsSlack(false);
    }
  };

  const handleSaveTwilio = async () => {
    setSavingTwilio(true);
    try {
      await axios.put(`${API}/admin/app-settings/twilio`, {
        from_number: twilioFromInput.trim(),
        enabled: twilio.enabled,
      });
      setTwilio((t) => ({ ...t, from_number: twilioFromInput.trim() }));
      toast.success("Twilio settings saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Twilio settings.");
    } finally {
      setSavingTwilio(false);
    }
  };

  const handleToggleTwilio = async (enabled) => {
    setTwilio((t) => ({ ...t, enabled }));
    try {
      await axios.put(`${API}/admin/app-settings/twilio`, { enabled });
    } catch (error) {
      toast.error("Failed to update.");
      setTwilio((t) => ({ ...t, enabled: !enabled }));
    }
  };

  const handleTestTwilio = async () => {
    if (!twilioTestNumber.trim()) {
      toast.error("Enter a number to text.");
      return;
    }
    setTestingTwilio(true);
    try {
      await axios.post(`${API}/admin/sms/test`, { to_number: twilioTestNumber.trim() });
      toast.success("Test text sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test text.");
    } finally {
      setTestingTwilio(false);
    }
  };

  const handleSaveOrderMore = async () => {
    if (!orderMoreEmailInput.trim()) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSavingOrderMore(true);
    try {
      await axios.put(`${API}/admin/app-settings/order-more-email`, {
        email: orderMoreEmailInput.trim()
      });
      setOrderMoreEmail(orderMoreEmailInput.trim());
      toast.success("Order more notification email updated!");
    } catch (error) {
      console.error("Failed to save order more email:", error);
      toast.error("Failed to save email.");
    } finally {
      setSavingOrderMore(false);
    }
  };

  const handleSaveApprovals = async () => {
    if (!approvalsEmailInput.trim()) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSavingApprovals(true);
    try {
      await axios.put(`${API}/admin/app-settings/approvals-email`, {
        email: approvalsEmailInput.trim()
      });
      setApprovalsEmail(approvalsEmailInput.trim());
      toast.success("Approvals notification email updated!");
    } catch (error) {
      console.error("Failed to save approvals email:", error);
      toast.error("Failed to save email.");
    } finally {
      setSavingApprovals(false);
    }
  };

  const handleSaveReactivation = async () => {
    if (!reactivationEmailInput.trim()) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSavingReactivation(true);
    try {
      await axios.put(`${API}/admin/app-settings/reactivation-email`, {
        email: reactivationEmailInput.trim()
      });
      setReactivationEmail(reactivationEmailInput.trim());
      toast.success("Reactivation notification email updated!");
    } catch (error) {
      console.error("Failed to save reactivation email:", error);
      toast.error("Failed to save email.");
    } finally {
      setSavingReactivation(false);
    }
  };

  const handleSaveTermsEmail = async () => {
    if (!termsEmailInput.trim()) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSavingTermsEmail(true);
    try {
      await axios.put(`${API}/admin/app-settings/terms-accepted-email`, {
        email: termsEmailInput.trim()
      });
      setTermsEmail(termsEmailInput.trim());
      toast.success("Terms accepted notification email updated!");
    } catch (error) {
      toast.error("Failed to save email.");
    } finally {
      setSavingTermsEmail(false);
    }
  };

  const handleSaveNoLeads = async () => {
    setSavingNoLeads(true);
    try {
      await axios.put(`${API}/admin/app-settings/no-leads-alert-email`, { email: noLeadsEmailInput.trim() });
      setNoLeadsEmail(noLeadsEmailInput.trim());
      toast.success("No-lead alert email updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save the no-lead alert email.");
    } finally {
      setSavingNoLeads(false);
    }
  };

  const updateSection = (i, field, val) =>
    setSections((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));
  const addSection = () => setSections((prev) => [...prev, { heading: "", body: "" }]);
  const removeSection = (i) => setSections((prev) => prev.filter((_, idx) => idx !== i));
  const moveSection = (i, dir) =>
    setSections((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const handleSaveTerms = async () => {
    const cleaned = sections
      .map((s) => ({ heading: (s.heading || "").trim(), body: s.body || "" }))
      .filter((s) => s.heading || s.body.trim());
    if (cleaned.length === 0) {
      toast.error("Add at least one section.");
      return;
    }
    setSavingTerms(true);
    try {
      await axios.put(`${API}/admin/app-settings/terms`, { sections: cleaned });
      setSectionsSaved(JSON.stringify(sections));
      toast.success("How it Works saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save How it Works.");
    } finally {
      setSavingTerms(false);
    }
  };

  const handleSaveCrEmail = async (type) => {
    const email = crEmailInputs[type]?.trim();
    if (!email) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setCrSaving(prev => ({ ...prev, [type]: true }));
    try {
      await axios.put(`${API}/admin/app-settings/change-request-emails`, {
        [type]: email
      });
      setCrEmails(prev => ({ ...prev, [type]: email }));
      toast.success(`${CHANGE_REQUEST_TYPES.find(t => t.key === type)?.label} email updated!`);
    } catch (error) {
      console.error(`Failed to save ${type} email:`, error);
      toast.error("Failed to save email.");
    } finally {
      setCrSaving(prev => ({ ...prev, [type]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings size={24} className="text-[#f4cc1e]" />
        <h1 className="text-2xl font-bold text-white font-['Chivo']">Settings</h1>
      </div>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white">Change Request Routing</CardTitle>
          <p className="text-xs text-white/50 mt-1">Each change request type is emailed to a separate address. Use commas to send to multiple people.</p>
        </CardHeader>
        <CardContent className="px-5 py-2">
          {CHANGE_REQUEST_TYPES.map(({ key, label, description, icon }) => (
            <EmailConfigRow
              key={key}
              label={label}
              description={description}
              icon={icon}
              value={crEmailInputs[key] || ""}
              onChange={(val) => setCrEmailInputs(prev => ({ ...prev, [key]: val }))}
              onSave={() => handleSaveCrEmail(key)}
              saving={crSaving[key] || false}
              originalValue={crEmails[key] || ""}
            />
          ))}
        </CardContent>
      </Card>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white">Other Notifications</CardTitle>
          <p className="text-xs text-white/50 mt-1">Additional notification email settings</p>
        </CardHeader>
        <CardContent className="px-5 py-2">
          <EmailConfigRow
            label="Order More Leads"
            description="Lead order requests from customers"
            icon={ShoppingCart}
            value={orderMoreEmailInput}
            onChange={setOrderMoreEmailInput}
            onSave={handleSaveOrderMore}
            saving={savingOrderMore}
            originalValue={orderMoreEmail}
          />
          <EmailConfigRow
            label="Approvals Notifications"
            description="Where the Approvals page sends its outbound change-request emails (CC'd alongside the contractor)"
            icon={Inbox}
            value={approvalsEmailInput}
            onChange={setApprovalsEmailInput}
            onSave={handleSaveApprovals}
            saving={savingApprovals}
            originalValue={approvalsEmail}
          />
          <EmailConfigRow
            label="Reactivation Requests"
            description="Where inactive customers' 'Reactivate Account' requests are sent"
            icon={RotateCcw}
            value={reactivationEmailInput}
            onChange={setReactivationEmailInput}
            onSave={handleSaveReactivation}
            saving={savingReactivation}
            originalValue={reactivationEmail}
          />
          <EmailConfigRow
            label="Terms Accepted Notifications"
            description="Notified that callers can start calling when a customer accepts the Terms"
            icon={FileCheck}
            value={termsEmailInput}
            onChange={setTermsEmailInput}
            onSave={handleSaveTermsEmail}
            saving={savingTermsEmail}
            originalValue={termsEmail}
          />
          <EmailConfigRow
            label="No-Lead Alert (72h)"
            description="Daily digest of active campaigns with no leads in 72 hours"
            icon={AlertTriangle}
            value={noLeadsEmailInput}
            onChange={setNoLeadsEmailInput}
            onSave={handleSaveNoLeads}
            saving={savingNoLeads}
            originalValue={noLeadsEmail}
          />
        </CardContent>
      </Card>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white">Slack Notifications</CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Post a message to a Slack channel whenever a customer enters the support chat queue. Create an
            Incoming Webhook in Slack (apps → Incoming Webhooks), pick the channel, and paste the URL here.
            Leave blank to disable.
          </p>
        </CardHeader>
        <CardContent className="px-5 py-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4 border-b border-[#1a3a6a]">
            <div className="flex items-center gap-3 sm:w-64 flex-shrink-0">
              <div className="w-9 h-9 rounded-lg bg-[#f4cc1e]/10 flex items-center justify-center flex-shrink-0">
                <Hash size={16} className="text-[#f4cc1e]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">New Chat Alerts</p>
                <p className="text-xs text-white/50">Slack Incoming Webhook URL</p>
              </div>
            </div>
            <div className="flex flex-1 items-center gap-2">
              <Input
                type="text"
                value={slackWebhookInput}
                onChange={(e) => setSlackWebhookInput(e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                className="flex-1 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleTestSlack}
                disabled={testingSlack || !slackWebhook}
                title={slackWebhook ? "Send a test message" : "Save a webhook first"}
                className="h-9 border-[#1a3a6a] text-white hover:bg-[#0d2a5a]"
              >
                {testingSlack ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4">
            <div className="flex items-center gap-3 sm:w-64 flex-shrink-0">
              <div className="w-9 h-9 rounded-lg bg-[#f4cc1e]/10 flex items-center justify-center flex-shrink-0">
                <Mail size={16} className="text-[#f4cc1e]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Mention (optional)</p>
                <p className="text-xs text-white/50">Tag someone on each alert</p>
              </div>
            </div>
            <div className="flex flex-1 items-center gap-2">
              <Input
                type="text"
                value={slackMentionInput}
                onChange={(e) => setSlackMentionInput(e.target.value)}
                placeholder="Slack member ID (e.g. U01ABC234), or @here / @channel"
                className="flex-1 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
              />
              <Button
                size="sm"
                onClick={handleSaveSlack}
                disabled={
                  savingSlack ||
                  (slackWebhookInput.trim() === slackWebhook && slackMentionInput.trim() === slackMention)
                }
                className="h-9 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
              >
                {savingSlack ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </Button>
            </div>
          </div>
          <p className="text-xs text-white/40 pb-3">
            To find a member ID in Slack: click the person → <span className="text-white/60">View full profile</span> → the
            ⋮ menu → <span className="text-white/60">Copy member ID</span>. A plain name like “@Vanessa Castro” won’t
            notify her — Slack needs the ID.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white flex items-center gap-2">
            <Smartphone size={16} className="text-[#f4cc1e]" /> Text Messaging (Twilio)
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Two-way SMS from the "Text Messages" section.
            {twilio.credentials_configured
              ? " Twilio credentials are configured."
              : " ⚠️ Twilio credentials (TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN) are NOT set on the server."}
          </p>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 sm:w-64 flex-shrink-0">
              <div className="w-9 h-9 rounded-lg bg-[#f4cc1e]/10 flex items-center justify-center flex-shrink-0">
                <Smartphone size={16} className="text-[#f4cc1e]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Sending Number</p>
                <p className="text-xs text-white/50">Your dedicated Twilio number</p>
              </div>
            </div>
            <div className="flex flex-1 items-center gap-2">
              <Input
                type="text"
                value={twilioFromInput}
                onChange={(e) => setTwilioFromInput(e.target.value)}
                placeholder="+18775551234"
                className="flex-1 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
              />
              <Button
                size="sm"
                onClick={handleSaveTwilio}
                disabled={savingTwilio || twilioFromInput.trim() === (twilio.from_number || "")}
                className="h-9 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
              >
                {savingTwilio ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-[#1a3a6a] pt-4">
            <div>
              <p className="text-sm font-medium text-white">Enabled</p>
              <p className="text-xs text-white/50">Turn text messaging on/off</p>
            </div>
            <button
              type="button"
              onClick={() => handleToggleTwilio(!twilio.enabled)}
              className={`h-6 w-11 rounded-full transition-colors relative ${twilio.enabled ? "bg-[#f4cc1e]" : "bg-[#1a3a6a]"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${twilio.enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>

          <div className="border-t border-[#1a3a6a] pt-4 space-y-2">
            <Label className="text-white/80 text-sm">Send a test text</Label>
            <div className="flex gap-2">
              <Input
                value={twilioTestNumber}
                onChange={(e) => setTwilioTestNumber(e.target.value)}
                placeholder="(704) 555-1234"
                className="flex-1 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleTestTwilio}
                disabled={testingTwilio}
                className="h-9 border-[#1a3a6a] text-white hover:bg-[#0d2a5a]"
              >
                {testingTwilio ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} className="mr-1" />} Test
              </Button>
            </div>
          </div>

          <div className="border-t border-[#1a3a6a] pt-4">
            <p className="text-xs text-white/50">
              In the Twilio console, set your number's <span className="text-white/70">Messaging → "A message comes in"</span> webhook (HTTP POST) to:
            </p>
            <code className="block mt-1 text-xs text-[#f4cc1e] bg-[#0d2a5a] rounded px-2 py-1.5 break-all">
              {`${process.env.REACT_APP_BACKEND_URL}/api/twilio/sms/inbound`}
            </code>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white flex items-center gap-2">
            <Smartphone size={16} className="text-[#f4cc1e]" /> SMS Slack Notifications
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Send text-message alerts (new inbound texts, unanswered replies) to their own Slack channel,
            separate from support chat. Leave the webhook blank to fall back to the support-chat channel.
          </p>
        </CardHeader>
        <CardContent className="px-5 py-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4 border-b border-[#1a3a6a]">
            <div className="flex items-center gap-3 sm:w-64 flex-shrink-0">
              <div className="w-9 h-9 rounded-lg bg-[#f4cc1e]/10 flex items-center justify-center flex-shrink-0">
                <Hash size={16} className="text-[#f4cc1e]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Text Alerts Channel</p>
                <p className="text-xs text-white/50">Slack Incoming Webhook URL</p>
              </div>
            </div>
            <div className="flex flex-1 items-center gap-2">
              <Input type="text" value={smsSlackWebhookInput} onChange={(e) => setSmsSlackWebhookInput(e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                className="flex-1 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30" />
              <Button size="sm" variant="outline" onClick={handleTestSmsSlack} disabled={testingSmsSlack}
                title="Send a test message" className="h-9 border-[#1a3a6a] text-white hover:bg-[#0d2a5a]">
                {testingSmsSlack ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4">
            <div className="flex items-center gap-3 sm:w-64 flex-shrink-0">
              <div className="w-9 h-9 rounded-lg bg-[#f4cc1e]/10 flex items-center justify-center flex-shrink-0">
                <Mail size={16} className="text-[#f4cc1e]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Mention (optional)</p>
                <p className="text-xs text-white/50">Tag someone on each text alert</p>
              </div>
            </div>
            <div className="flex flex-1 items-center gap-2">
              <Input type="text" value={smsSlackMentionInput} onChange={(e) => setSmsSlackMentionInput(e.target.value)}
                placeholder="Slack member ID (e.g. U01ABC234), or @here / @channel"
                className="flex-1 h-9 text-sm bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30" />
              <Button size="sm" onClick={handleSaveSmsSlack}
                disabled={savingSmsSlack || (smsSlackWebhookInput.trim() === smsSlackWebhook && smsSlackMentionInput.trim() === smsSlackMention)}
                className="h-9 bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90">
                {savingSmsSlack ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </Button>
            </div>
          </div>
          <p className="text-xs text-white/40 pb-3">
            Use a different channel's webhook here to keep text alerts out of the support-chat channel. Same
            member-ID rule as above — a plain name won't ping.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white flex items-center gap-2">
            <MessageSquare size={16} className="text-[#f4cc1e]" /> Support Chat Auto-Reply
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            When a customer starts a new chat we automatically reply. We send the business-hours
            message during the hours below, and the after-hours message otherwise.
          </p>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-5">
          {/* Business hours config */}
          <div className="space-y-3">
            <Label className="text-white/80">Business days</Label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((d) => {
                const on = autoReply.business_days.includes(d.code);
                return (
                  <button
                    key={d.code}
                    type="button"
                    onClick={() => toggleDay(d.code)}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                      on
                        ? "bg-[#f4cc1e] text-[#091c3c] border-[#f4cc1e] font-semibold"
                        : "bg-[#0d2a5a] text-white/70 border-[#1a3a6a] hover:border-[#f4cc1e]/50"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="space-y-1.5">
              <Label className="text-white/80">Open</Label>
              <Input
                type="time"
                value={autoReply.business_open}
                onChange={(e) => setAutoReply((p) => ({ ...p, business_open: e.target.value }))}
                className="h-9 w-32 bg-[#0d2a5a] border-[#1a3a6a] text-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Close</Label>
              <Input
                type="time"
                value={autoReply.business_close}
                onChange={(e) => setAutoReply((p) => ({ ...p, business_close: e.target.value }))}
                className="h-9 w-32 bg-[#0d2a5a] border-[#1a3a6a] text-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Timezone</Label>
              <Select
                value={autoReply.business_timezone}
                onValueChange={(v) => setAutoReply((p) => ({ ...p, business_timezone: v }))}
              >
                <SelectTrigger className="h-9 w-48 bg-[#0d2a5a] border-[#1a3a6a] text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                  {allowedTzs.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {TZ_LABELS[tz] || tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Messages */}
          <div className="space-y-1.5">
            <Label className="text-white/80">Business-hours message</Label>
            <Textarea
              value={autoReply.hours_message}
              onChange={(e) => setAutoReply((p) => ({ ...p, hours_message: e.target.value }))}
              rows={5}
              placeholder="Shown during business hours…"
              className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/80">After-hours message</Label>
            <Textarea
              value={autoReply.afterhours_message}
              onChange={(e) => setAutoReply((p) => ({ ...p, afterhours_message: e.target.value }))}
              rows={5}
              placeholder="Shown outside business hours…"
              className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30 text-sm"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveAutoReply}
              disabled={savingAutoReply}
              className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold"
            >
              {savingAutoReply ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Save Auto-Reply
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white flex items-center gap-2">
            <FileText size={16} className="text-[#f4cc1e]" /> How it Works
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Shown to customers on first login — they must confirm each section before the portal unlocks.
            Emojis and line breaks are preserved exactly as typed.
          </p>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-4">
          {sections.length === 0 && (
            <p className="text-sm text-white/50">No sections yet. Add one to get started.</p>
          )}
          {sections.map((s, i) => (
            <div key={i} className="rounded-lg border border-[#1a3a6a] bg-[#0d2a5a] p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/40 w-6 shrink-0">#{i + 1}</span>
                <input
                  value={s.heading}
                  onChange={(e) => updateSection(i, "heading", e.target.value)}
                  placeholder="Section heading"
                  className="flex-1 bg-[#091c3c] border border-[#1a3a6a] rounded px-2 py-1 text-sm text-white placeholder:text-white/30"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-white/60 hover:text-white h-8 px-2">↑</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="text-white/60 hover:text-white h-8 px-2">↓</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeSection(i)} className="text-red-400 hover:text-red-300 h-8 px-2">Remove</Button>
              </div>
              <Textarea
                value={s.body}
                onChange={(e) => updateSection(i, "body", e.target.value)}
                rows={6}
                placeholder="Section text…"
                className="resize-y bg-[#091c3c] border-[#1a3a6a] text-white placeholder:text-white/30 text-sm leading-relaxed"
              />
            </div>
          ))}
          <div className="flex items-center justify-between">
            <Button type="button" variant="outline" onClick={addSection} className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a]">+ Add Section</Button>
            <Button
              onClick={handleSaveTerms}
              disabled={savingTerms || JSON.stringify(sections) === sectionsSaved}
              className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold"
            >
              {savingTerms ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Save How it Works
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardHeader className="py-4 px-5 border-b border-[#1a3a6a]">
          <CardTitle className="font-['Chivo'] text-base text-white flex items-center gap-2">
            <FileText size={18} className="text-[#f4cc1e]" /> QuickBooks Online
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-4">
          <p className="text-sm text-white/60">
            Connect RoofNuts' QuickBooks company to create invoices and read payment status.
          </p>
          {qbo.status === "connected" && (
            <div className="flex items-center gap-3">
              <span className="text-green-400 text-sm font-medium">Connected (realm {qbo.realm_id})</span>
              <Button variant="outline" onClick={disconnectQbo} className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a]">Disconnect</Button>
            </div>
          )}
          {qbo.status === "reconnect_needed" && (
            <div className="flex items-center gap-3">
              <span className="text-amber-400 text-sm font-medium">Reconnect needed</span>
              <Button onClick={connectQbo} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">Reconnect QuickBooks</Button>
            </div>
          )}
          {(qbo.status === "disconnected" || qbo.status === "loading") && (
            <Button onClick={connectQbo} disabled={qbo.status === "loading"} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">
              Connect to QuickBooks
            </Button>
          )}

          {qbo.status === "connected" && (
            <div className="border-t border-[#1a3a6a] pt-4 space-y-3">
              <p className="text-sm text-white font-medium">Create Invoice</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input placeholder="Company name" value={inv.company_name}
                  onChange={(e) => setInv({ ...inv, company_name: e.target.value })}
                  className="bg-[#091c3c] border-[#1a3a6a] text-white placeholder:text-white/30" />
                <Input placeholder="Description" value={inv.description}
                  onChange={(e) => setInv({ ...inv, description: e.target.value })}
                  className="bg-[#091c3c] border-[#1a3a6a] text-white placeholder:text-white/30" />
                <Input type="number" placeholder="Quantity" value={inv.quantity}
                  onChange={(e) => setInv({ ...inv, quantity: e.target.value })}
                  className="bg-[#091c3c] border-[#1a3a6a] text-white placeholder:text-white/30" />
                <Input type="number" placeholder="Unit price" value={inv.unit_price}
                  onChange={(e) => setInv({ ...inv, unit_price: e.target.value })}
                  className="bg-[#091c3c] border-[#1a3a6a] text-white placeholder:text-white/30" />
                <Input placeholder="QBO Item Id (optional)" value={inv.item_id}
                  onChange={(e) => setInv({ ...inv, item_id: e.target.value })}
                  className="bg-[#091c3c] border-[#1a3a6a] text-white placeholder:text-white/30" />
              </div>
              <Button disabled={invSubmitting || !inv.company_name} onClick={createInvoice}
                className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold">
                {invSubmitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Create Invoice
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
